import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import TeamList from '../../components/TeamList';
import TeamRegistrationDialog from '../../components/TeamRegistrationDialog';
import {
  fetchTournament,
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  fetchAllUsers,
  Tournament,
  Team,
  User,
  CreateTeamData,
} from '../../services/api';

const TournamentTeamsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [newTeamName, setNewTeamName] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    setLoading(true);
    setError('');
    
    try {
      const [tournamentData, teamsData, usersData] = await Promise.all([
        fetchTournament(id),
        fetchTeams(id),
        fetchAllUsers(),
      ]);
      
      setTournament(tournamentData);
      setTeams(teamsData.teams);
      setAvailablePlayers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (teamData: CreateTeamData) => {
    if (!id) return;
    
    try {
      await createTeam(id, teamData);
      setSuccess('Team registered successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err: any) {
      throw err; // Let dialog handle the error
    }
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    setNewTeamName(team.team_name);
    setEditDialogOpen(true);
  };

  const handleUpdateTeam = async () => {
    if (!editingTeam) return;
    
    try {
      await updateTeam(editingTeam.id, { team_name: newTeamName });
      setSuccess('Team updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setEditDialogOpen(false);
      setEditingTeam(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this team? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteTeam(teamId);
      setSuccess('Team deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete team');
    }
  };

  const supportsDoubles = tournament?.match_type_config === 'doubles' || tournament?.match_type_config === 'mixed_doubles';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/tournaments/${id}`)}
          sx={{ mb: 2 }}
        >
          Back to Tournament
        </Button>

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/tournaments"
            onClick={(e) => {
              e.preventDefault();
              navigate('/tournaments');
            }}
          >
            Tournaments
          </Link>
          <Link
            color="inherit"
            href={`/tournaments/${id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/tournaments/${id}`);
            }}
          >
            {tournament?.name || 'Loading...'}
          </Link>
          <Typography color="text.primary">Teams</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Tournament Teams
          </Typography>
          {supportsDoubles && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setRegistrationDialogOpen(true)}
            >
              Register Team
            </Button>
          )}
        </Box>

        {tournament && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {tournament.name} • {tournament.match_type_config === 'mixed_doubles' ? 'Mixed Doubles' : tournament.match_type_config === 'doubles' ? 'Doubles' : 'Singles'}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!supportsDoubles && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This tournament does not support doubles matches. Only singles matches are allowed.
        </Alert>
      )}

      <TeamList
        teams={teams}
        loading={loading}
        error={error}
        onEdit={handleEditTeam}
        onDelete={handleDeleteTeam}
        showActions={true}
        allowFiltering={true}
      />

      {/* Team Registration Dialog */}
      <TeamRegistrationDialog
        open={registrationDialogOpen}
        onClose={() => setRegistrationDialogOpen(false)}
        onSubmit={handleCreateTeam}
        availablePlayers={availablePlayers}
        doublesConfig={tournament?.doubles_config}
      />

      {/* Edit Team Name Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Team Name"
            fullWidth
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTeam} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TournamentTeamsPage;
